import JsPDF from "jspdf";
import { formatFeeName } from "@/utilities/settings";
import writtenNumber from "written-number";

const pdfHeader = (settings, options = {}) => {
  // Destructure options with defaults
  const {
    nomClient = null,
    title = "",
    subTitle = null,
    portrait = true,
    existingDoc = null,
    showDate = true,
    locale = "fr-FR",
    logoSize = 20,
  } = options;

  let arrayOfImages = settings.image;
  let imgData = new Image();

  for (let i = 0; i < arrayOfImages.length; i++) {
    if (settings.activeBuilding === arrayOfImages[i].db) {
      imgData.src = arrayOfImages[i].logo;
      break; // Stop looping once the correct image is found
    }
  }

  const doc = existingDoc || new JsPDF(portrait ? "portrait" : "landscape");
  doc.setFont("Amiri");

  const date = new Date();
  const dateOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  // Center the title
  const pageWidth = doc.internal.pageSize.getWidth();

  if (title) {
    const titleWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - titleWidth) / 2;

    doc.setFontSize(15);
    doc.setFont("Amiri", "Bold");
    doc.text(titleX, 22, title); // Use calculated x-coordinate for centering
    doc.setFont("Amiri", "normal");
  }

  if (subTitle) {
    // Set font size first
    doc.setFontSize(12);

    // Calculate maximum available width (page width minus margins)
    const maxWidth = pageWidth - 30; // 15px margin on each side

    // Split subtitle into multiple lines if it's too long
    const subTitleLines = doc.splitTextToSize(subTitle, maxWidth);

    // Starting Y position for subtitle
    let subTitleY = 29;
    const lineHeight = 5; // Space between lines

    // Draw each line, centered
    subTitleLines.forEach((line, index) => {
      const lineWidth = doc.getTextWidth(line);
      const subTitleX = (pageWidth - lineWidth) / 2;
      doc.text(subTitleX, subTitleY + index * lineHeight, line);
    });
  }
  if (nomClient) doc.text(15, 35, `Nom et Prénom: ${nomClient}`);
  doc.setFontSize(10);

  // Check if language is Arabic (RTL)
  const isArabic = locale === "ar-AR";

  try {
    if (imgData.src) {
      // Position logo based on language direction
      const logoX = isArabic ? pageWidth - (logoSize + 15) : 15; // Right for Arabic, left for others
      doc.addImage(imgData, "JPEG", logoX, 5, logoSize, logoSize);
    }
  } catch (error) {
    console.log(error);
  }

  if (showDate) {
    doc.setFontSize(8);
    const dateText = date.toLocaleDateString(locale, dateOptions);
    const dateTextWidth = doc.getTextWidth(dateText);
    // Center date vertically with logo (logo is at y=5, center is at y=5 + logoSize/2)
    const logoCenterY = 5 + logoSize / 2;
    // Position date based on language direction, vertically centered with logo
    const dateX = isArabic ? 15 : pageWidth - dateTextWidth - 15; // Left for Arabic, right for others
    doc.text(dateX, logoCenterY, dateText);
  }

  return doc;
};
const printTransaction = (record, settings, locale = "fr-FR") => {
  const title =
    record.typeFR == "F"
      ? "Facture N. " + record.serial
      : "Reçue N. " + record.serial;
  const doc = pdfHeader(settings, {
    nomClient: record.nomClient,
    title: title,
    locale: locale,
  });

  var arr = [];
  const array = [];

  let onTransReduction = record.onTransReduction
    ? record.onTransReduction / 100
    : 0;

  let repReduction = record.payment.repReduction;
  let baseReduction = false;
  if (repReduction) {
    for (const month in record.info.months) {
      if (Number(repReduction[month])) {
        baseReduction = true;
        break;
      }
    }
    if (record.onInscription && repReduction.inscription) baseReduction = true;
  }

  if (record.fees) {
    Object.keys(record.fees).forEach((k) => {
      arr.push(record.student.lastName + (record.student.middleName ? " " + record.student.middleName : "") + " " + record.student.firstName);

      let feeName = formatFeeName(k, record.daily);

      arr.push(feeName);
      arr.push(record.typeReglement);

      const fee = record.fees[k];

      if (baseReduction) {
        arr.push(
          (repReduction[k] ? Number(repReduction[k]).toFixed(2) : 0) + "%"
        );
      }
      let montantCol = record.typeFR == "F" ? fee - fee * record.tva : fee;
      arr.push(montantCol.toFixed(2));

      array.push(arr);
      arr = [];
    });
  }
  let rowData = [
    record.montantHT,
    record.montantTTC - record.montantHT - record.fiscal,
    record.fiscal,
    record.montantTTC,
  ];

  //affichier reduction si existe
  if (onTransReduction) {
    if (record.typeFR == "R") {
      arr.push({
        colSpan: baseReduction ? 4 : 3,
        content: "Total sans réduction",
        styles: { valign: "middle", halign: "center", fontStyle: "Bold" },
      });
      arr.push({
        content: (record.montantTTC / (1 - onTransReduction)).toFixed(2),
        styles: { valign: "middle", halign: "center", fontStyle: "Bold" },
      });
      array.push(arr);
      arr = [];
    }
    arr.push({
      colSpan: baseReduction ? 4 : 3,
      content: "Réduction",
      styles: { valign: "middle", halign: "center", fontStyle: "Bold" },
    });
    arr.push({
      content: (onTransReduction * 100).toFixed(2) + "%",
      styles: { valign: "middle", halign: "center", fontStyle: "Bold" },
    });
    array.push(arr);
    arr = [];
  }
  let rowDataText = ["Total HTVA", "TVA", "Timbre", "Total TTC"];
  for (let i = 0; i < 4; i++) {
    if (record.typeFR == "R") {
      i = 3;
      rowDataText[i] = "Total";
    }
    arr.push({
      colSpan: baseReduction ? 4 : 3,
      content: rowDataText[i],
      styles: { valign: "middle", halign: "center", fontStyle: "Bold" },
    });

    arr.push({
      content: parseFloat(rowData[i]).toFixed(2),
      styles: { valign: "middle", halign: "center", fontStyle: "Bold" },
    });
    array.push(arr);
    arr = [];
  }

  doc.autoTable({
    styles: {
      lineWidth: 0.2,
      valign: "middle",
      halign: "center",
      fontStyle: "Bold",
      font: "Amiri",
    },
    startY: doc.pageCount > 1 ? doc.autoTableEndPosY() + 15 : 38,
    head: baseReduction
      ? [["Enfant", "Frais", "Désignation", "réduction de base", "Montant"]]
      : [["Enfant", "Frais", "Désignation", "Montant"]],
    body: array,
    foot: [
      [
        {
          colSpan: baseReduction ? 5 : 4,
          content:
            "Le montant total est " +
            writtenNumber(record.montantTTC, { lang: "fr" }) +
            " DT",
          styles: { halign: "center" },
        },
      ],
    ],
  });
  doc.save(title + ".pdf");
};
export { pdfHeader, printTransaction };
